import { useTranslation } from 'react-i18next';
import { Navigate, Outlet, useParams } from 'react-router-dom';
import DeploymentHistory from './DeploymentHistory/DeploymentHistory';
import ContentHeader from 'components/UI/ContentHeader/ContentHeader';
import {
  FRONT_ROUTER,
  DEPLOYMENT_ALL_PREFIX,
  DEPLOYMENT_TAB_PARAMS_KEY,
  DEPLOYMENT_PARTIAL_PREFIX,
} from 'constants/router';
import DeploymentService, {
  CanDeployApiResponse,
} from 'services/DeploymentService';
import DeploymentForm from './DeploymentForm/DeploymentForm';
import PartialDeploymentTab from './PartialDeployment/PartialDeployment';
import { useState, useEffect } from 'react';
import Spinner from 'components/UI/Spinner/Spinner';
import CanNotDeploy from './CanNotDeploy';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { ReactQueryKeys } from 'constants/react-query-keys';

/**
 * @description Navigation links for deployment tabs
 * @type {Array<{title: string, url: string}>}
 */
export const links = [
  {
    title: 'deployment.tab_all_devices',
    url: FRONT_ROUTER.DEPLOYMENT_FULL_DEPLOYMENT_TAB,
  },
  {
    title: 'deployment.tab_selected_device',
    url: FRONT_ROUTER.DEPLOYMENT_PARTIAL_DEPLOYMENT_TAB,
  },
];

/**
 * @description Deployment component that handles the deployment workflow.
 * Displays different tabs for full or partial deployment based on URL parameters.
 * Shows deployment form if deployment is allowed, otherwise displays reasons why deployment is not possible.
 *
 * @SADtrace SAD-003-6.1, SAD-003-6.2, SAD-003-6.3
 *
 * @History
 * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
 * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
 * 01-06-2026 aleksandar dukleski: DLA-1456 disable refetch on window focus for deployment check
 *
 * @returns {JSX.Element} The rendered Deployment component
 */
const Deployment = () => {
  const { t } = useTranslation();
  const [canDeploy, setCanDeploy] = useState(false);
  const [
    libraryHasNotChangedSinceLastDeployment,
    setLibraryHasNotChangedSinceLastDeployment,
  ] = useState(false);
  const [canNotDeployReasons, setCanNotDeployReasons] = useState<
    CanDeployApiResponse['reasons']
  >({
    panelLockPasscode: false,
    advancedSettingsPasscode: false,
    incompleteDrugs: false,
    incompleteCareArea: false,
  });

  const { [DEPLOYMENT_TAB_PARAMS_KEY]: deploymentTab } = useParams();

  /**
   * @description Query to check if deployment is allowed
   * Sets state variables based on API response
   */
  const {
    data,
    isLoading: canDeployLoading,
    isFetching,
    failureReason,
  } = useQuery({
    queryKey: [
      ReactQueryKeys.CAN_DEPLOY,
      {
        deploymentTab,
      },
    ],

    queryFn: () => DeploymentService.canDeploy(deploymentTab),
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (!data) return;
    setCanDeploy(data.data.status);
    setLibraryHasNotChangedSinceLastDeployment(
      data.data.libraryHasNotChangedSinceLastDeployment,
    );
    setCanNotDeployReasons(data.data.reasons);
  }, [data]);

  useEffect(() => {
    if (failureReason) {
      toast.error(
        (failureReason as any).response?.data?.message || t('errors.default'),
      );
    }
  }, [failureReason]);

  return (
    <div>
      <h4 data-testid="deployment-page">{t('deployment.title')}</h4>
      <div className="d-flex gap-3 pt-5">
        <section className="flex-fill" style={{ minWidth: '50%' }}>
          <ContentHeader links={links} />
          <div className="color-bg px-4 h-100 position-relative">
            <>
              {canDeployLoading || isFetching ? <Spinner isAbsolute /> : null}

              {deploymentTab === DEPLOYMENT_ALL_PREFIX ? (
                canDeployLoading || isFetching ? (
                  <Spinner isAbsolute />
                ) : canDeploy ? (
                  <DeploymentForm
                    libraryHasNotChangedSinceLastDeployment={
                      libraryHasNotChangedSinceLastDeployment
                    }
                  />
                ) : (
                  <CanNotDeploy reasons={canNotDeployReasons} />
                )
              ) : deploymentTab === DEPLOYMENT_PARTIAL_PREFIX ? (
                canDeployLoading || isFetching ? (
                  <Spinner isAbsolute />
                ) : canDeploy ? (
                  <PartialDeploymentTab
                    libraryHasNotChangedSinceLastDeployment={
                      libraryHasNotChangedSinceLastDeployment
                    }
                  />
                ) : (
                  <CanNotDeploy reasons={canNotDeployReasons} />
                )
              ) : (
                <>{<Navigate to={FRONT_ROUTER.ERROR} />}</>
              )}
            </>
            <Outlet />
          </div>
        </section>
        <DeploymentHistory />
      </div>
    </div>
  );
};

export default Deployment;
